import { isArray } from '@chakra-ui/utils';
import { Editor, JSONContent, getAttributes, isMarkActive } from '@tiptap/core';
import { WIDGET_DEFAULT_FONT } from 'constants/fonts';
import cloneDeep from 'lodash.clonedeep';
import { UnsavedPatch } from 'modules/Editor/store/history/history.types';
import { Mark, Node } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { FocusEvent } from 'react';
import { HyperLink, WidgetType } from 'types/widget.types';
import { findAllPropertyValues } from 'utils/findAllPropertyValues';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { combineUnsavedPatchesToMap, parsePathToMap } from '../../../libs/e2api/Client.helpers';
import {
  MARK_ITEM_BOLD,
  MARK_ITEM_ITALIC,
  MARK_ITEM_LINK,
  MARK_ITEM_UNDERLINE,
  MIN_FONT_SIZE,
  TEXT_TAG_TO_STYLES_MAP,
} from './TextBasedWidgets.config';
import { ListAttributes, TextBasedWidgetData, TextTagStyleConfig, TextWidgetTag } from './TextBasedWidgets.types';

/**
 * Converts text style config to array of prosemirror mark data
 *
 * @param style TextTagStyleConfig - style config
 * @returns Prosemirror mark array
 */
export const convertStyleToProseMirrorMark = (style: TextTagStyleConfig, hasLink: boolean = false) => {
  const { fontFamily, isBold, isItalic, fontSize, color, isUnderline } = style;

  const mark = [{ type: 'textStyle', attrs: { fontFamily, fontSize: `${fontSize}px`, color } }] as {
    type: string;
    attrs?: { [key: string]: any };
  }[];

  if (isBold) mark.push(MARK_ITEM_BOLD);
  if (isItalic) mark.push(MARK_ITEM_ITALIC);
  if (isUnderline) mark.push(MARK_ITEM_UNDERLINE);
  if (hasLink) mark.push(MARK_ITEM_LINK);

  return mark;
};

/**
 * Searches text nodes for the nearest previous marks in the editor
 *
 * @param state Prosemirror editor state
 * @param tag type of text DOM tag
 * @returns marks[]
 */
export const getNearestMarksFromSelection = (state: EditorState, tag?: TextWidgetTag) => {
  if (!tag) tag = TextWidgetTag.Paragraph;
  const { selection, doc } = state;
  const { $from } = selection;
  const $node = doc.resolve($from.pos);

  const pos = $node.pos;

  return getNearestMarks(state, pos, tag);
};

export const getNearestMarks = (state: EditorState, position: number, tag?: TextWidgetTag) => {
  const { schema, doc } = state;
  let marks: readonly Mark[] = [];

  // iterate back through text widget until styles are found
  for (let i = position; !marks.length && i >= 0; i--) {
    const node = doc.nodeAt(i);
    if (node) {
      marks = node.marks;
    }
  }

  // if no marks backward -> iterate forward through the text widget until styles are found
  if (!marks.length) {
    for (let i = position; !marks.length && i <= state.doc.content.size; i++) {
      const node = doc.nodeAt(i);
      if (node) marks = node.marks;
    }
  }

  // use default styles for specific text tag
  if (!marks.length) {
    if (!tag) tag = TextWidgetTag.Paragraph;
    const textStyle = TEXT_TAG_TO_STYLES_MAP[tag];
    marks = [
      schema.marks.textStyle.create({
        fontSize: `${textStyle.fontSize}px`,
        color: textStyle.color,
        fontFamily: textStyle.fontFamily,
      }),
    ];
  }

  return marks;
};

/**
 * Is the mark a text style mark
 * @param mark prosemirror mark
 * @returns boolean
 */
export const isTextStyleMark = (mark: Mark) => mark.type.name === 'textStyle';

/**
 * Is the JSONContent mark a text style mark
 * @param mark prosemirror mark
 * @returns boolean
 */
export const isTextStyleContentMark = (mark: JSONContent) => mark.type === 'textStyle';

const getScaledFontSizePx = (fontSize: number, ratio: number, options?: FontResizeOptions): string => {
  const scaledFontSize = fontSize * ratio;
  const newFontSize = Math.max(MIN_FONT_SIZE, options?.round ? Math.round(scaledFontSize) : scaledFontSize);

  // if the font scales down to the min font size
  if (fontSize >= MIN_FONT_SIZE && newFontSize === MIN_FONT_SIZE && options?.onReachMinFontSize) {
    // x is inversed divide by 1 to get the ratio
    const maxRatio = 1 / (newFontSize / fontSize);
    options?.onReachMinFontSize(maxRatio);
  }

  return newFontSize + 'px';
};

/**
 * Find the largest mark font size from a node
 * @param node prosemirror node or tiptap json content
 *
 * @returns number
 */
export const findLargestChildMarkFontSize = (node: JSONContent | Node) => {
  let fontSize = MIN_FONT_SIZE;

  if (!node) return fontSize;

  // no text / no children in the node -> use node fontSize
  if (!node.content) return node?.attrs?.fontSize ? parseFloat(node.attrs.fontSize) : MIN_FONT_SIZE;
  if (node instanceof Node) {
    if (!node.textContent) return node?.attrs?.fontSize ? parseFloat(node.attrs.fontSize) : MIN_FONT_SIZE;
  }

  node.content.forEach((node) => {
    const marks = node.marks || [];
    let markFontSize = null;
    if (marks[0] instanceof Mark) {
      markFontSize = (marks as Mark[]).find(isTextStyleMark)?.attrs?.fontSize;
    } else {
      markFontSize = (marks as JSONContent[]).find(isTextStyleContentMark)?.attrs?.fontSize;
    }

    // find the biggest child font size
    const markFontSizeNumber = parseFloat(markFontSize);
    if (markFontSize && !Number.isNaN(markFontSizeNumber)) {
      fontSize = Math.max(markFontSizeNumber, fontSize);
    }
  });

  return fontSize;
};

/**
 * Find the list font size attribute
 * @param node prosemirror node or tiptap json content
 *
 * @returns number
 */
const findListFontSize = (node: JSONContent) => {
  let nodeFs = node?.attrs?.fontSize ? parseFloat(node?.attrs?.fontSize) : null;

  // if the list node has no children
  // and the list node has no fontSize attribute
  if (!node.firstChild && node.content && !nodeFs) {
    const firstContent = node.content[0];
    const isParagraph = firstContent?.type === 'paragraph';
    if (isParagraph && firstContent.attrs?.fontSize) {
      return parseFloat(firstContent.attrs.fontSize);
    }
  }

  // Find the font size from the first child text node
  let child = node.firstChild;
  while (!!child && !child.isText) {
    child = child.firstChild;
  }

  if (child && child.marks) {
    const markFs = child.marks.find(isTextStyleContentMark)?.attrs?.fontSize;
    if (markFs) nodeFs = parseFloat(markFs);
  }

  return nodeFs || MIN_FONT_SIZE;
};

type FontResizeOptions = {
  round?: boolean;
  // callback that is called when the font size reaches the minimum font size
  onReachMinFontSize?: (ratio: number) => void;
};
/**
 * Modifies all font sizes by the specified ratio
 * @param node prosemirror node
 * @param ratio value by which to increase each font size
 * @returns updated data
 */
// TODO: this can probably be optimized a bit for better performance
export const modifyAllFontSizes = (node: JSONContent, ratio: number, options?: FontResizeOptions): JSONContent => {
  const newNode = cloneDeep(node);

  // find the attribute font size (paragraph or listItem nodes)
  let attributeFontSize = null;
  if (newNode?.type === 'paragraph') attributeFontSize = findLargestChildMarkFontSize(newNode);
  if (newNode?.type === 'listItem') attributeFontSize = findListFontSize(newNode);

  // update the attribute font size
  if (typeof attributeFontSize === 'number') {
    if (!newNode.attrs) newNode.attrs = {};
    newNode.attrs.fontSize = getScaledFontSizePx(attributeFontSize, ratio, options);
  }

  if (newNode.content) {
    // recursively modify all children font sizes (a node can have multiple different font sizes)
    newNode.content = newNode.content.map((subNode) => modifyAllFontSizes(subNode, ratio, options));
  } else if (newNode.marks) {
    const textStyle = newNode.marks.find(isTextStyleContentMark) as JSONContent;
    if (textStyle && textStyle.attrs) {
      textStyle.attrs.fontSize = getScaledFontSizePx(parseFloat(textStyle.attrs.fontSize), ratio, options);
    }
  }

  return newNode;
};

/**
 * Check if the selection includes a list node
 * -> selection is at the start of a list item
 *
 * @param state EditorState
 * @returns boolean
 */
export const selectionHasListNode = (state: EditorState, nodeType: any) => {
  let hasListNode = false;

  const max = state.doc.content.size - 2;
  state.selection.ranges.forEach((range) => {
    const from = Math.min(range.$from.pos, max);
    const to = Math.min(range.$to.pos, max);

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (nodeType && nodeType === node.type) {
        hasListNode = true;
      }
    });
  });

  return hasListNode;
};

/**
 * Get li attributes from a list of marks
 *
 * @param marks [] Prosemirror marks
 * @returns {}
 */
export const marksToListAttr = (marks: readonly Mark[]) => {
  const attr: ListAttributes = { bold: false, italic: false };

  if (!marks || !marks.length) return attr;

  const fontStyle = marks.find((mark) => mark.type.name === 'textStyle');
  const isBold = marks.find((mark) => mark.type.name === 'bold');
  const isItalic = marks.find((mark) => mark.type.name === 'italic');

  if (isBold) attr.bold = true;
  if (isItalic) attr.italic = true;

  if (fontStyle) {
    const { fontSize, fontFamily, color } = fontStyle.attrs;
    if (fontSize) attr.fontSize = fontSize;
    if (fontFamily) attr.fontFamily = fontFamily;
    if (color) attr.color = color;
  }

  return attr;
};

/**
 * Get the list attribute that has changed between the new and old state
 * (Only one attribute can be changed at a time)
 *
 * @param newState EditorState
 * @param oldState EditorState
 * @returns list attributes | null
 */
export const getListAttributeChanges = (oldState: EditorState, newState: EditorState) => {
  let attributes: null | { bold?: boolean; italic?: boolean; color?: string; fontSize?: string; fontFamily?: string } =
    null;

  const isBold = isMarkActive(newState, 'bold');
  const isItalic = isMarkActive(newState, 'italic');
  const textStyle = getAttributes(newState, 'textStyle');

  const oldIsBold = isMarkActive(oldState, 'bold');
  const oldIsItalic = isMarkActive(oldState, 'italic');
  const oldTextStyle = getAttributes(oldState, 'textStyle');

  if (isBold !== oldIsBold) return { bold: isBold };
  if (isItalic !== oldIsItalic) return { italic: isItalic };
  if (textStyle.color !== oldTextStyle.color) return { color: textStyle.color };
  if (textStyle.fontSize !== oldTextStyle.fontSize) return { fontSize: textStyle.fontSize };
  if (textStyle.fontFamily !== oldTextStyle.fontFamily) return { fontFamily: textStyle.fontFamily };

  return attributes;
};

/**
 * Find the last node by name from the selection
 * hasNode should be used to check if a node was found
 *
 * @param state EditorState
 * @param name The name of the node to find (e.g. "paragraph", "listItem")
 * @returns { pos: number, node: Node, hasNode: boolean }
 */
export const findNodeFromSelection = (state: EditorState, name: string) => {
  let foundPosition: number | null = null;
  let foundNode: Node | null = null;

  state.selection.ranges.forEach((range) => {
    const from = range.$from.pos;
    const to = range.$to.pos;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === name) {
        foundPosition = pos;
        foundNode = node;
      }
    });
  });

  const hasNode = typeof foundPosition === 'number';

  // convert to node and position -> hasNode is used to check if a node was found
  // TODO: maybe an easier way to do this?
  return { pos: foundPosition as unknown as number, node: foundNode as unknown as Node, hasNode };
};

/**
 * Get the raw text from the text widget
 * All html tag will stripe out and up to 100 length by default.
 *
 * @param proseMirrorData - JSONContent
 * @param maxLength  - Max text length
 * @param hasMaxLength - If true, text will be cut by maxLength
 * @returns  Raw text (string without html tags)
 */

// matches html tags
const HTML_TAG_REGEX = /(<([^>]+)>)/gi;

export const getRawText = (proseMirrorData: JSONContent, maxLength = 100, hasMaxLength = true): string => {
  if (!proseMirrorData || !proseMirrorData.content) return '';

  const texts = findAllPropertyValues(proseMirrorData, ['text']);
  if (!texts.length) return '';

  const parsedText = texts
    .map((text) => String(text))
    .join('')
    .replace(HTML_TAG_REGEX, '');

  if (!hasMaxLength) return parsedText;

  return parsedText.substring(0, maxLength);
};

/**
 * When tabbing in the colorpicker with selected text,
 * prevent the focus from moving out of the colorpicker to the editor
 *
 * @param e - FocusEvent
 * @param isOpen - If true, color picker is open
 * @returns  TextTagStyleConfig
 */
export const onColorPickerContainerBlur = (e: FocusEvent, isOpen: boolean) => {
  if (isOpen) {
    e.stopPropagation();
    const target = e.target as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;

    // reset the focus to the color picker with a timeout
    if (relatedTarget && relatedTarget?.className.includes('ProseMirror')) {
      setTimeout(() => target.focus(), 0);
    }
  }
};

/**
 * Get all the unique colors as RGB from the text/table widget that are rendered on the page
 *
 * @returns  { [key: string]: boolean }
 * e.g. { 'rgb(0, 200, 0)': true, 'rgb(39, 255, 255)': true }
 */
export const getAllTextBasedWidgetColors = () => {
  // Select all <p> elements and their children within elements with the class 'ProseMirror'
  const paragraphElements = document.querySelectorAll('.ProseMirror p, .ProseMirror p *');
  const uniqueColors: { [key: string]: boolean } = {};

  paragraphElements.forEach((element) => {
    const computedStyle = window.getComputedStyle(element);
    const color = computedStyle.getPropertyValue('color');
    if (color) uniqueColors[color] = true;
  });

  return uniqueColors;
};

/**
 * Find the first font family of the Editor content
 * @param editor
 * @returns font family as a string (defaults to Inter if no family is found)
 */
export const findFirstFontFamily = (editor: Editor | null) => {
  if (!editor) return WIDGET_DEFAULT_FONT;

  const { doc } = editor.state;
  let firstFontFamily: string | null = null;

  // iterate over all text nodes
  doc.nodesBetween(0, doc.content.size, (node) => {
    // Get the first font family from the node marks
    const marks = node.marks;
    const markFontFamily = marks.find(isTextStyleMark)?.attrs.fontFamily;
    if (markFontFamily && !firstFontFamily) firstFontFamily = markFontFamily;
  });

  return firstFontFamily || WIDGET_DEFAULT_FONT;
};

/**
 * Helper to check if a patch has link
 *
 * @param proseMirrorData
 * @returns boolean - if the patch has any link
 */
export const widgetHasLink = (proseMirrorData: JSONContent) => {
  const { content } = proseMirrorData;
  if (!content) return false;

  let hasLink: boolean = false;
  content.forEach((node) => {
    if (node?.marks) {
      const linkMark = node.marks.find((mark) => mark.type === 'link');
      if (linkMark) {
        const url = linkMark.attrs?.href || null;
        if (url) hasLink = true;
      }
    }

    // tables have many content
    if (node?.content && !hasLink) {
      hasLink = widgetHasLink(node);
    }
  });

  return hasLink;
};

/**
 * it validates if the patch is a link and has a link change if is tru we check for phishing
 *
 * @param unsavedPatches patch that will be saved
 * @param isPhishing boolean
 * @returns boolean
 */
export const patchesHasLink = (unsavedPatches: UnsavedPatch[], isPhishing = false) => {
  let hasLink = false;

  try {
    const unsavedPatchesMap = combineUnsavedPatchesToMap(unsavedPatches);

    Object.keys(unsavedPatchesMap).forEach((path) => {
      const { id: targetId } = parsePathToMap(path);
      const widgetType = getWidgetTypeFromId(targetId);
      const patches = unsavedPatchesMap[path]?.patches || [];

      if ([WidgetType.Icon, WidgetType.Image].includes(widgetType)) {
        hasLink = !!patches.find((node) => {
          if (node.op === 'remove') return true;
          if (node.op === 'add') {
            if (node?.value?.hyperlink) {
              const value = node.value as HyperLink;
              return !!value.hyperlink;
            }
          }
          return node.path.includes('hyperlink');
        });
      }

      if ([WidgetType.Text, WidgetType.Table].includes(widgetType)) {
        patches.forEach((node) => {
          if (node?.op === 'remove' && isPhishing) {
            hasLink = true;
            return;
          }
          let value = node.value as JSONContent[];

          if (node?.op === 'add') {
            value = (node.value as TextBasedWidgetData).proseMirrorData?.content as JSONContent[];
          }
          if (value && isArray(value)) {
            value.forEach((node: JSONContent) => {
              if (widgetHasLink(node) || isPhishing) {
                hasLink = true;
              }
            });
          }
        });
        return hasLink;
      }
    });
  } catch (error) {
    // in case some issue happens
    // this protect the code on saving if any error happens
    console.log('error on checking link', error);
  }

  return hasLink;
};
